import React, { useState } from 'react';
import { SeniorityLevel } from '../../types/onboarding';

interface Step3SeniorityProps {
  seniority: SeniorityLevel | null;
  years_of_experience: number | null;
  onUpdate: (seniority: SeniorityLevel, years_of_experience: number | null) => void;
  onNext: () => void;
  onBack: () => void;
}

export const Step3Seniority: React.FC<Step3SeniorityProps> = ({
  seniority,
  years_of_experience,
  onUpdate,
  onNext,
  onBack,
}) => {
  const [error, setError] = useState('');
  const [localYears, setLocalYears] = useState<string>(years_of_experience?.toString() || '');

  const seniorityOptions = [
    { value: SeniorityLevel.JUNIOR, label: 'Junior', description: 'Entry level positions' },
    { value: SeniorityLevel.MID_LEVEL, label: 'Mid-Level', description: '2-5 years experience' },
    { value: SeniorityLevel.SENIOR, label: 'Senior', description: '5+ years experience' },
    { value: SeniorityLevel.STAFF, label: 'Manager', description: 'Leadership and managing roles' },
    { value: SeniorityLevel.C_LEVEL, label: 'C-Suite', description: 'Executive level' },
  ];

  const handleNext = () => {
    if (!seniority) {
      setError('Please select your seniority level');
      return;
    }
    if (!localYears || parseInt(localYears) < 0) {
      setError('Please enter your years of experience');
      return;
    }
    setError('');
    onNext();
  };

  const handleSelect = (level: SeniorityLevel) => {
    setError('');
    onUpdate(level, localYears ? parseInt(localYears) : null);
  };

  const handleYearsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers and empty string
    if (value === '' || /^\d+$/.test(value)) {
      setLocalYears(value);
      onUpdate(seniority!, value ? parseInt(value) : null);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">What's your seniority level?</h1>

      <div className="space-y-4 md:space-y-6">
        {/* Seniority Level Dropdown */}
        <div>
          <label htmlFor="seniority" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
            Seniority Level <span className="text-red-500">*</span>
          </label>
          <select
            id="seniority"
            value={seniority || ''}
            onChange={(e) => {
              const value = e.target.value as SeniorityLevel;
              if (value) {
                handleSelect(value);
              }
            }}
            className={`w-full px-3 md:px-4 py-2.5 md:py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors text-sm md:text-base ${
              error && !seniority
                ? 'border-red-500 focus:ring-red-200'
                : 'border-gray-300 focus:ring-blue-200 focus:border-blue-500'
            }`}
          >
            <option value="">Select your seniority level</option>
            {seniorityOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label} — {option.description}
              </option>
            ))}
          </select>
        </div>

        {/* Years of Experience Input */}
        <div>
          <label htmlFor="years_of_experience" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
            Years of Experience <span className="text-red-500">*</span>
          </label>
          <input
            id="years_of_experience"
            type="number"
            min="0"
            max="50"
            value={localYears}
            onChange={handleYearsChange}
            placeholder="e.g., 5"
            className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base"
          />
        </div>

        {error && <p className="text-xs md:text-sm text-red-600 text-center">{error}</p>}

        <div className="flex gap-3 md:gap-4">
        <button
          onClick={onBack}
            className="flex-1 bg-white text-gray-700 py-2.5 md:py-3 px-4 md:px-6 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-sm md:text-base"
        >
          Back
        </button>
        <button
          onClick={handleNext}
            className="flex-1 bg-blue-600 text-white py-2.5 md:py-3 px-4 md:px-6 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-sm md:text-base"
        >
          Continue
        </button>
        </div>
      </div>
    </div>
  );
};

